import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { PlusIcon, MinusIcon } from '@heroicons/react/24/outline';
import {
  Stage,
  Layer,
  Image as KonvaImage,
  Group,
  Circle,
  Text,
} from 'react-konva';
import useImage from 'use-image';
import { Modal, ModalEyebrow, ModalFoot } from 'components/Modal';

const SpaceModal = ({
  spaces,
  active,
  placements = [],
  floorplanUrl,
  onClose,
  onPlaceChange,
  bookedPlacementIds = [],
  selectedPlace: selectedPlaceProp,
}) => {
  const { t } = useTranslation();
  const stageRef = useRef(null);
  const canvasHostRef = useRef(null);
  const hasFloorplan = !!floorplanUrl;

  const [image] = useImage(floorplanUrl);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [hoveredPlace, setHoveredPlace] = useState(null);
  const [circleLabelOption, setCircleLabelOption] = useState('name'); // name | size | price
  const [hostSize, setHostSize] = useState({ width: 800, height: 600 });
  const [enrichedPlacements, setEnrichedPlacements] = useState([]);
  const [zoomScale, setZoomScale] = useState(1);

  // allow parent to control current selection (optional)
  useEffect(() => {
    if (selectedPlaceProp) setSelectedPlace(selectedPlaceProp);
  }, [selectedPlaceProp]);

  // Track the canvas pane size so 100% zoom means "the whole plan fits".
  // Rendering at the image's natural size made large scans open as a
  // scroll-hunt into one corner.
  useEffect(() => {
    const host = canvasHostRef.current;
    if (!host) return;
    const ro = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      if (rect?.width && rect?.height) {
        setHostSize({ width: rect.width, height: rect.height });
      }
    });
    ro.observe(host);
    return () => ro.disconnect();
    // Re-attach when the floorplan pane mounts (modal open / floorplan load)
  }, [hasFloorplan, active]);

  const naturalW = image?.naturalWidth || 800;
  const naturalH = image?.naturalHeight || 600;
  const fitScale = Math.min(
    1,
    hostSize.width / naturalW,
    hostSize.height / naturalH
  );

  const zoomIn = () => setZoomScale((z) => Math.min(4, +(z + 0.2).toFixed(2)));
  const zoomOut = () =>
    setZoomScale((z) => Math.max(0.5, +(z - 0.2).toFixed(2)));
  const resetZoom = () => setZoomScale(1);

  const handleCircleClick = (place) => {
    if (place?.isOccupied) return; // do not allow selecting a taken dot
    if (!place?.spaceTypeId) return; // dot no longer matches a space — unbookable
    setSelectedPlace(place);
  };
  const handleRowClick = (place) => setSelectedPlace(place);

  const handleConfirmAction = () => {
    onPlaceChange?.(selectedPlace);
    onClose?.();
  };

  // Build enriched floorplan points so we carry both:
  // - placementId (UUID for the dot)  -> used to paint & lock the dot
  // - spaceTypeId (DB int for Space)  -> used as booking spaceId
  useEffect(() => {
    if (!Array.isArray(spaces) || !Array.isArray(placements)) {
      setEnrichedPlacements([]);
      return;
    }

    const enriched = placements.map((p) => {
      const matchedSpace = spaces.find(
        (s) => s.id === p.spaceId || s.name === p.spaceName
      );

      const placementId = p.id; // UUID from floorplan editor
      const spaceTypeId = matchedSpace?.id ?? null; // DB integer id

      return {
        ...p,
        ...matchedSpace,
        id: placementId,
        placementId,
        spaceTypeId,
        name: matchedSpace?.name ?? p.spaceName ?? '',
        price: matchedSpace?.price ?? p.price ?? null,
        size: matchedSpace?.size ?? p.size ?? null,
        description: matchedSpace?.description ?? p.description ?? '',
        isOccupied: bookedPlacementIds.includes(placementId),
      };
    });

    setEnrichedPlacements(enriched);
  }, [spaces, placements, bookedPlacementIds]);

  // Space types whose floorplan dots are ALL occupied: quantity may still be
  // positive (Pending doesn't consume inventory), but auto-assign would 409
  // with "no free placements" — grey the row out instead of failing late.
  const fullyOccupiedSpaceIds = useMemo(() => {
    const bySpace = new Map(); // spaceTypeId -> { total, taken }
    for (const p of enrichedPlacements) {
      if (!p.spaceTypeId) continue;
      const entry = bySpace.get(p.spaceTypeId) || { total: 0, taken: 0 };
      entry.total += 1;
      if (p.isOccupied) entry.taken += 1;
      bySpace.set(p.spaceTypeId, entry);
    }
    const full = new Set();
    for (const [id, { total, taken }] of bySpace) {
      if (total > 0 && taken >= total) full.add(id);
    }
    return full;
  }, [enrichedPlacements]);

  const scaledW = naturalW * fitScale * zoomScale;
  const scaledH = naturalH * fitScale * zoomScale;

  // Centered, flipping, clamped tooltip (editorial). Falls back to the
  // selected dot so touch users (no hover) still see the details.
  const infoPlace =
    hoveredPlace ||
    (selectedPlace && typeof selectedPlace.xPercent === 'number'
      ? selectedPlace
      : null);

  const renderInfoWindow = () => {
    if (!infoPlace) return null;

    const TOOLTIP_W = 240;
    const TOOLTIP_H = 150;
    const PAD = 12;
    const GAP = 16;

    const x = infoPlace.xPercent * scaledW;
    const y = infoPlace.yPercent * scaledH;

    const placeAbove = y - TOOLTIP_H - GAP >= 0;
    const rawTop = placeAbove ? y - GAP : y + GAP;
    const translateY = placeAbove ? '-100%' : '0%';

    const minLeft = PAD + TOOLTIP_W / 2;
    const maxLeft = scaledW - PAD - TOOLTIP_W / 2;
    const clampedLeft = Math.max(minLeft, Math.min(x, maxLeft));

    return (
      <div
        className="absolute z-20 w-[240px] bg-white border border-line rounded-xl shadow-modal px-4 py-3.5"
        style={{
          top: rawTop,
          left: clampedLeft,
          transform: `translate(-50%, ${translateY})`,
          willChange: 'transform, top, left',
        }}
      >
        <div className="font-display font-bold text-sm text-ink">
          {infoPlace.name}
        </div>
        <div className="flex gap-3.5 mt-2 text-xs text-muted-2">
          <span>
            {t('event_create.size')}{' '}
            <span className="text-ink font-semibold">
              {infoPlace.size != null ? `${infoPlace.size} m²` : '—'}
            </span>
          </span>
          <span>
            {t('common.price')}{' '}
            <span className="text-ink font-semibold">
              {infoPlace.price != null ? `€${infoPlace.price}` : '—'}
            </span>
          </span>
          <span>
            <span
              className="font-semibold"
              style={{
                color:
                  infoPlace.isOccupied || !infoPlace.spaceTypeId
                    ? 'var(--bad, #A12E2E)'
                    : 'var(--good, #2C7A4B)',
              }}
            >
              {infoPlace.isOccupied
                ? t('space.legend.taken', 'Taken')
                : !infoPlace.spaceTypeId
                  ? t('space.legend.unavailable', 'Unavailable')
                  : t('space.legend.available', 'Available')}
            </span>
          </span>
        </div>
        {infoPlace.description && (
          <p className="mt-2 pt-2 border-t border-line text-[12.5px] leading-[1.4] text-muted-2 font-serif italic">
            {infoPlace.description}
          </p>
        )}
      </div>
    );
  };

  const labelFor = (place) => {
    if (circleLabelOption === 'name') {
      return (place.name || '').slice(0, 2).toUpperCase();
    }
    if (circleLabelOption === 'size') {
      return place.size?.toString() || '';
    }
    if (circleLabelOption === 'price') {
      return place.price != null ? `${Math.round(place.price)}` : '';
    }
    return '';
  };

  return (
    <Modal
      isOpen={active}
      onClose={onClose}
      size="xl"
      className="h-[min(820px,92vh)]"
    >
      {/* Head */}
      <div className="flex items-center gap-4 px-6 py-4 border-b border-line bg-paper">
        <div>
          <ModalEyebrow>{t('space.choose', 'Choose your space')}</ModalEyebrow>
          <div className="font-serif italic text-[15px] text-muted-2 mt-0.5">
            {t('event_detail.pick_plan', 'Available spaces')}
          </div>
        </div>
      </div>

      {/* Body */}
      <div
        className={`grid flex-1 min-h-0 ${
          hasFloorplan ? 'grid-cols-1 md:grid-cols-[360px_1fr]' : 'grid-cols-1'
        }`}
      >
        {/* LEFT — list rail */}
        <div className="flex flex-col min-h-0 border-b md:border-b-0 md:border-r border-line bg-white">
          <div className="px-[22px] pt-[18px] pb-3.5 border-b border-line">
            <div className="font-display text-xs font-bold tracking-[0.12em] uppercase text-ink">
              {t('space.list_head', 'All spaces')}
            </div>
            <p className="mt-1.5 text-[12.5px] leading-[1.45] text-muted-2">
              {t(
                'space.hint_random',
                'Choosing a space type from this list will assign you a random spot of that type.'
              )}
              {hasFloorplan && (
                <>
                  {' '}
                  {t(
                    'space.hint_floorplan',
                    'To pick a specific placement, select it directly on the floor plan.'
                  )}
                </>
              )}
            </p>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto px-3.5 py-3 flex flex-col gap-2">
            {(spaces || []).map((s) => {
              // Highlight the row both for a direct list pick (id match) and
              // for a floorplan-dot pick of this type (spaceTypeId match).
              const isSel =
                selectedPlace?.id === s.id ||
                selectedPlace?.spaceTypeId === s.id;
              const isAvailable =
                Number(s?.quantity) > 0 && !fullyOccupiedSpaceIds.has(s.id);
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => handleRowClick(s)}
                  disabled={!isAvailable}
                  className={`relative grid grid-cols-[28px_1fr_auto] gap-3.5 items-center text-left
                              border rounded-xl px-4 py-3.5 transition ${
                                isSel
                                  ? 'bg-brand-softer border-brand ring-2 ring-brand'
                                  : !isAvailable
                                    ? 'bg-white border-line opacity-60 cursor-not-allowed'
                                    : 'bg-white border-line hover:bg-paper'
                              }`}
                >
                  <span
                    className={`w-[18px] h-[18px] rounded-full self-start mt-0.5 border-2 border-white ${
                      isSel
                        ? 'bg-brand ring-2 ring-ink'
                        : isAvailable
                          ? 'bg-good ring-1 ring-line-strong'
                          : 'bg-bad ring-1 ring-line-strong'
                    }`}
                  />
                  <div className="min-w-0">
                    <div className="font-display font-bold text-[15px] text-ink tracking-[-0.005em]">
                      {s?.name}
                    </div>
                    {s?.description && (
                      <div className="text-[12.5px] text-muted-2 mt-0.5 leading-[1.4] line-clamp-2">
                        {s.description}
                      </div>
                    )}
                    <div className="flex gap-2.5 mt-2 text-xs text-muted">
                      {s?.size != null && <span>{s.size} m²</span>}
                      <span className={isAvailable ? 'text-good' : 'text-bad'}>
                        {isAvailable
                          ? t('space.legend.available', 'Available')
                          : t('space.legend.taken', 'Taken')}
                      </span>
                    </div>
                  </div>
                  <div className="text-right font-display font-bold text-base text-ink">
                    {s?.price != null ? `€${s.price}` : '—'}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* RIGHT — floorplan canvas */}
        {hasFloorplan && (
          <div className="relative flex flex-col min-h-0 overflow-hidden bg-paper-2">
            {/* TL: label-mode segment */}
            <div className="absolute top-[18px] left-[18px] z-30 flex gap-2 items-center">
              <div className="flex bg-white border border-line rounded-full p-[3px] shadow-soft">
                {[
                  { k: 'name', label: t('space.label_mode.id', 'ID') },
                  { k: 'size', label: t('space.label_mode.size', 'Size') },
                  { k: 'price', label: t('space.label_mode.price', 'Price') },
                ].map((m) => (
                  <button
                    key={m.k}
                    type="button"
                    onClick={() => setCircleLabelOption(m.k)}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-full transition ${
                      circleLabelOption === m.k
                        ? 'bg-ink text-white'
                        : 'text-muted-2'
                    }`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </div>

            {/* BL: legend */}
            <div className="absolute bottom-[18px] left-[18px] z-30">
              <div className="inline-flex items-center gap-3.5 bg-white border border-line rounded-full px-4 py-2 shadow-soft text-xs font-semibold text-ink">
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-good border border-white ring-1 ring-line-strong" />
                  {t('space.legend.available', 'Available')}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-bad border border-white ring-1 ring-line-strong" />
                  {t('space.legend.taken', 'Taken')}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-brand border border-white ring-1 ring-line-strong" />
                  {t('space.legend.selected', 'Selected')}
                </span>
              </div>
            </div>

            {/* BR: zoom stack */}
            <div className="absolute bottom-[18px] right-[18px] z-30">
              <div className="inline-flex bg-white border border-line rounded-full shadow-soft overflow-hidden">
                <button
                  type="button"
                  onClick={zoomOut}
                  aria-label={t('common.zoom_out', 'Zoom out')}
                  className="w-[34px] h-[34px] grid place-items-center text-ink hover:bg-paper-2"
                >
                  <MinusIcon className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={resetZoom}
                  aria-label={t('common.reset_zoom', 'Reset zoom')}
                  className="px-3 h-[34px] text-xs font-semibold text-ink border-l border-r border-line hover:bg-paper-2"
                >
                  {Math.round(zoomScale * 100)}%
                </button>
                <button
                  type="button"
                  onClick={zoomIn}
                  aria-label={t('common.zoom_in', 'Zoom in')}
                  className="w-[34px] h-[34px] grid place-items-center text-ink hover:bg-paper-2"
                >
                  <PlusIcon className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Canvas */}
            <div
              ref={canvasHostRef}
              className={`flex-1 min-h-0 overflow-auto p-[22px] ${
                zoomScale === 1
                  ? 'flex justify-center items-start'
                  : 'block'
              }`}
            >
              <div
                className="relative"
                style={{ width: `${scaledW}px`, height: `${scaledH}px` }}
              >
                <Stage
                  ref={stageRef}
                  width={scaledW}
                  height={scaledH}
                  className="border border-line-strong bg-white rounded-2xl"
                >
                  <Layer>
                    {image && (
                      <KonvaImage
                        image={image}
                        width={scaledW}
                        height={scaledH}
                        listening={false}
                      />
                    )}

                    {enrichedPlacements.map((place, index) => {
                      const isSelected = selectedPlace?.id === place.id;
                      const isOccupied = place?.isOccupied;
                      // Dots whose space row disappeared (deleted/renamed)
                      // can't be booked — render them neutral, not green.
                      const isOrphan = !place?.spaceTypeId;
                      const x = place.xPercent * scaledW;
                      const y = place.yPercent * scaledH;
                      const label = labelFor(place);

                      return (
                        <Group
                          key={`placement-${
                            place.id || place.placementId || index
                          }`}
                          onMouseEnter={() => setHoveredPlace(place)}
                          onMouseLeave={() => setHoveredPlace(null)}
                          onClick={() => handleCircleClick(place)}
                          onTap={() => handleCircleClick(place)}
                          listening
                        >
                          <Circle
                            x={x}
                            y={y}
                            radius={12}
                            fill={
                              isSelected
                                ? '#FFBD59'
                                : isOccupied
                                  ? '#A12E2E'
                                  : isOrphan
                                    ? '#9ca3af'
                                    : '#2C7A4B'
                            }
                            stroke="#ffffff"
                            strokeWidth={2}
                          />
                          {label && (
                            <Text
                              x={x}
                              y={y}
                              text={label}
                              fontSize={10}
                              fill={isSelected ? '#1A1A1F' : '#ffffff'}
                              fontStyle="bold"
                              align="center"
                              verticalAlign="middle"
                              offsetX={7}
                              offsetY={5}
                            />
                          )}
                        </Group>
                      );
                    })}
                  </Layer>
                </Stage>

                {/* Tooltip overlays inside the same positioning context */}
                {renderInfoWindow()}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <ModalFoot>
        {selectedPlace ? (
          <>
            <div className="flex flex-col gap-0.5">
              <div className="font-display text-[10.5px] tracking-[0.12em] uppercase text-muted font-bold">
                {t('space.selected', 'Selected')}
              </div>
              <div className="font-display text-base font-bold text-ink">
                {selectedPlace.name}
              </div>
            </div>
            {selectedPlace?.size != null && (
              <>
                <div className="w-px h-9 bg-line" />
                <div className="flex flex-col gap-0.5">
                  <div className="font-display text-[10.5px] tracking-[0.12em] uppercase text-muted font-bold">
                    {t('event_create.size', 'Size')}
                  </div>
                  <div className="font-display text-base font-bold text-ink">
                    {selectedPlace.size} m²
                  </div>
                </div>
              </>
            )}
            <div className="ml-auto flex items-center gap-4 font-display">
              <div>
                <div className="text-[26px] font-bold tracking-[-0.01em] text-ink leading-none">
                  €{selectedPlace.price}
                </div>
              </div>
              <button
                type="button"
                onClick={handleConfirmAction}
                className="rounded-full bg-brand text-ink font-semibold text-sm px-6 py-3.5 hover:bg-brand-hover transition"
              >
                {t('space.book', 'Book this space')}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="flex flex-col gap-0.5">
              <div className="font-display text-[10.5px] tracking-[0.12em] uppercase text-muted font-bold">
                {t('space.selected', 'Selected')}
              </div>
              <div className="font-serif italic text-muted">
                {t('space.pick_to_continue', 'Pick a stall to continue')}
              </div>
            </div>
            <button
              type="button"
              disabled
              className="ml-auto rounded-full bg-brand text-ink font-semibold text-sm px-6 py-3.5 opacity-40 pointer-events-none"
            >
              {t('space.book', 'Book this space')}
            </button>
          </>
        )}
      </ModalFoot>
    </Modal>
  );
};

export default SpaceModal;
